import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import api from "../lib/api";
import AppointmentForm from "./AppointmentForm";
import HandoffForm from "./HandoffForm";

const QUICK_REPLIES = [
  "Book an appointment",
  "What are your timings?",
  "Consultation fee?",
  "Where are you located?",
  "Dental implant cost?",
];

function BotMsg({ content, action }) {
  const isEmergency = action === "emergency";
  return (
    <div
      data-testid="bot-msg"
      className={isEmergency ? "sd-msg-emergency" : "sd-msg sd-msg-bot"}
    >
      {isEmergency && (
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Urgent — please read</div>
      )}
      {content}
    </div>
  );
}

function UserMsg({ content }) {
  return (
    <div data-testid="user-msg" className="sd-msg sd-msg-user">
      {content}
    </div>
  );
}

function SystemMsg({ content }) {
  return <div className="sd-msg-system">{content}</div>;
}

export default function ChatWidget({ startOpen = false, embedded = false }) {
  const [open, setOpen] = useState(startOpen);
  const [settings, setSettings] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [inlineForm, setInlineForm] = useState(null);
  const [language, setLanguage] = useState("english");
  const bodyRef = useRef(null);

  useEffect(() => {
    api.settings().then(setSettings).catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    if ((open || embedded) && !sessionId) {
      api.startSession().then((r) => {
        setSessionId(r.session_id);
        setMessages([{ role: "assistant", content: r.greeting }]);
      }).catch(() => {
        setMessages([{
          role: "assistant",
          content: "Sorry, I could not start a session. Please call +91 93216 36840.",
        }]);
      });
    }
  }, [open, embedded, sessionId]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, pending, inlineForm]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || !sessionId || pending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setPending(true);
    try {
      const resp = await api.sendMessage(sessionId, text);
      setLanguage(resp.language || "english");
      setMessages((m) => [
        ...m,
        { role: "assistant", content: resp.reply, action: resp.action },
      ]);
      if (resp.action === "book") setInlineForm("book");
      else if (resp.action === "handoff") setInlineForm("handoff");
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "I'm sorry, something went wrong. Please try again, or call +91 93216 36840.",
      }]);
    } finally {
      setPending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleQuick = (q) => {
    if (q === "Book an appointment") {
      setInlineForm("book");
      setMessages((m) => [
        ...m,
        { role: "user", content: q },
        {
          role: "assistant",
          content:
            "Sure — please share these details and our team will confirm on your number shortly.",
        },
      ]);
      return;
    }
    send(q);
  };

  const onBookingSuccess = (payload) => {
    setInlineForm(null);
    setMessages((m) => [
      ...m,
      { role: "system", content: "Appointment request sent" },
      {
        role: "assistant",
        content: `Thank you, ${payload.patient_name.split(" ")[0]}! Our team will confirm your slot shortly on ${payload.phone}. For anything urgent, please call +91 93216 36840.`,
      },
    ]);
  };

  const onHandoffSuccess = () => {
    setInlineForm(null);
    setMessages((m) => [
      ...m,
      { role: "system", content: "Details sent to the team" },
      {
        role: "assistant",
        content:
          "Thank you — someone from the clinic will get back to you shortly on the number you shared.",
      },
    ]);
  };

  const settingsData = settings || {};

  const header = (
    <div className="sd-panel-header">
      <div className="sd-avatar-dot">S</div>
      <div>
        <div className="sd-header-title">
          <span className="sd-word">Srinidhi</span>
          Dental &amp; Implant Clinic
        </div>
        <div className="sd-header-sub">— Assistant · Chembur Naka</div>
      </div>
      {!embedded && (
        <button
          data-testid="widget-close"
          className="sd-close-btn"
          onClick={() => setOpen(false)}
          aria-label="Close chat"
        >
          <X size={18} />
        </button>
      )}
      {embedded && (
        <button
          data-testid="widget-close"
          className="sd-close-btn"
          onClick={() => {
            try {
              window.parent.postMessage({ source: "srinidhi-chat", type: "close" }, "*");
            } catch (e) { /* ignore */ }
          }}
          aria-label="Close chat"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );

  const panel = (
    <div
      className="sd-panel"
      data-testid="chat-panel"
      role="dialog"
      aria-label="Srinidhi Dental Chat"
      style={embedded ? { position: "relative", right: "auto", bottom: "auto", width: "100%", height: "100%", maxWidth: "none", maxHeight: "none", borderRadius: 0, boxShadow: "none", border: 0 } : {}}
    >
      {header}

      <div className="sd-disclaimer" data-testid="widget-disclaimer">
        {settingsData.disclaimer || "Automated assistant — not medical advice."}
      </div>

      <div className="sd-body" ref={bodyRef} data-testid="chat-body">
        {messages.map((m, i) => {
          if (m.role === "user") return <UserMsg key={i} content={m.content} />;
          if (m.role === "system") return <SystemMsg key={i} content={m.content} />;
          return <BotMsg key={i} content={m.content} action={m.action} />;
        })}

        {pending && (
          <div className="sd-typing" data-testid="chat-typing">
            <span></span><span></span><span></span>
          </div>
        )}

        {inlineForm === "book" && sessionId && (
          <AppointmentForm
            sessionId={sessionId}
            language={language}
            onSuccess={onBookingSuccess}
            onCancel={() => setInlineForm(null)}
          />
        )}
        {inlineForm === "handoff" && sessionId && (
          <HandoffForm
            sessionId={sessionId}
            language={language}
            onSuccess={onHandoffSuccess}
            onCancel={() => setInlineForm(null)}
          />
        )}
      </div>

      {!inlineForm && messages.length <= 2 && (
        <div className="sd-quick-row" data-testid="quick-replies">
          {QUICK_REPLIES.map((q) => (
            <button
              key={q}
              className="sd-quick-btn"
              data-testid={`quick-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => handleQuick(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="sd-input-row">
        <textarea
          data-testid="chat-input"
          className="sd-input"
          rows={1}
          placeholder="Type your message… (English / हिन्दी / मराठी / தமிழ்)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={pending}
        />
        <button
          data-testid="chat-send"
          className="sd-send-btn"
          onClick={() => send()}
          disabled={pending || !input.trim()}
          aria-label="Send message"
        >
          <Send size={18} />
        </button>
      </div>

      <div className="sd-footer" data-testid="widget-footer">
        <span>
          <strong>Mon–Sat</strong> 10 AM–1:30 PM · 5–8:30 PM
        </span>
        <a
          data-testid="footer-call"
          href="tel:+919321636840"
          style={{
            color: "var(--brand-royal)",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontWeight: 500,
            textDecoration: "none",
          }}
        >
          <Phone size={12} /> +91 93216 36840
        </a>
      </div>
    </div>
  );

  if (embedded) {
    return panel;
  }

  return (
    <div className="sd-widget-shell" data-testid="chat-widget-root">
      {!open && (
        <button
          data-testid="chat-bubble"
          className="sd-bubble sd-bubble-pulse"
          onClick={() => setOpen(true)}
          aria-label="Open chat with Srinidhi Dental"
          style={{ position: "relative" }}
        >
          <MessageCircle size={26} />
        </button>
      )}
      {open && panel}
    </div>
  );
}
